import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PageHeader } from "../../components/PageHeader";
import {
  EngagementMetric,
  formatDuration,
  formatPercent,
} from "../../components/engagementFormat";
import { ownerAnalyticsApi } from "../../http/foyer";
import { tokens } from "../../theme";
import type {
  ContentAnalyticsAxes,
  ContentAnalyticsFilters,
  ContentAnalyticsOverview,
  Unavailable,
} from "@shared/types/contentAnalytics";
import type { ContentPlayCoverage } from "@shared/types/contentPlay";

/**
 * Audience — what a supplier's material actually did (`ANALYTICS-3`).
 *
 * ## Four perspectives, one set of numbers
 *
 * The same `content_play_daily` rows read four ways: by title, by package, by
 * supplier (which on this surface is one row — them), and by channel. They are
 * tabs rather than four pages because the *filters* are the point: a partner
 * asks "how did my catalogue do in Finnish care homes last quarter" once and
 * expects all four answers to move together.
 *
 * ## The three things this page must never do
 *
 * 1. **Draw a zero where there is an absence.** A day with no row is silence —
 *    an older TV bundle, a quiet property, a day retention already reaped — and
 *    a flat line at zero reads as "nobody watched". The coverage strip says
 *    which days actually carry data, and every unmeasurable roll-up prints its
 *    own reason instead of a number. Same discipline as `CONTENT-10`'s preview
 *    chip.
 * 2. **Call properties "locations" loosely.** `distinctPremises` counts
 *    PROPERTIES and the label says so. Room-level uniqueness is refused for
 *    good — see `@shared/types/contentPlay` — and a play count must never be
 *    presented as unique locations.
 * 3. **Let "country" read as a licensed market.** The axis is where the
 *    television stands, not where a title is licensed to be sold. The filter is
 *    labelled, and the server states `scope.geography` for the same reason.
 *
 * ## Impressions and plays are never added together
 *
 * An impression is the server witnessing a play URL being minted — somebody
 * asked to be able to watch this. A play is the device witnessing the picture
 * move. They are two questions, and this page keeps them in two columns.
 */

const RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
];

type TabKey = "assets" | "packages" | "channels" | "reach";

export default function SupplierAudiencePage() {
  const [filters, setFilters] = useState<ContentAnalyticsFilters>({ days: 30 });
  const [data, setData] = useState<ContentAnalyticsOverview | null>(null);
  const [axes, setAxes] = useState<ContentAnalyticsAxes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("assets");

  const set = useCallback(
    (patch: Partial<ContentAnalyticsFilters>) =>
      setFilters((current) => ({ ...current, ...patch })),
    []
  );

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        const [overview, pickers] = await Promise.all([
          ownerAnalyticsApi.contentPlayOverview(filters),
          ownerAnalyticsApi.contentPlayAxes({ days: filters.days }),
        ]);
        if (!live) return;
        setData(overview);
        setAxes(pickers);
        setError(null);
      } catch (e) {
        if (!live) return;
        const err = e as { code?: string; message: string };
        setError(
          err.code === "category_unavailable"
            ? "We could not ask the catalogue which titles carry that category, so this report would have been wrong rather than narrow. Try again, or clear the category."
            : err.message
        );
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [filters]);

  const totals = useMemo(() => {
    const rows = data?.asset.assets ?? [];
    return rows.reduce(
      (acc, row) => ({
        plays: acc.plays + row.plays,
        seconds: acc.seconds + row.secondsPlayed,
        impressions: acc.impressions + row.impressions,
        completions: acc.completions + row.completions,
      }),
      { plays: 0, seconds: 0, impressions: 0, completions: 0 }
    );
  }, [data]);

  const panel = {
    background: tokens.surface,
    border: `1px solid ${tokens.divider}`,
    borderRadius: 2,
    p: 2.5,
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Your account"
        title="Audience"
        subtitle="What your material played, how long people watched, and where."
      />

      <Box sx={panel}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Period"
            value={filters.days ?? 30}
            onChange={(e) => set({ days: Number(e.target.value) })}
            sx={{ minWidth: 170 }}
            inputProps={{ "data-testid": "audience-range" }}
          >
            {RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          {/* 🚨 "Country where it played", not "country where it is licensed".
              Two different questions that share one word, and a supplier who
              reads one as the other will believe we sold into a market we did
              not. The label carries the distinction; so does the help text. */}
          <TextField
            select
            size="small"
            label="Country of property"
            value={filters.country ?? ""}
            onChange={(e) => set({ country: e.target.value || undefined })}
            sx={{ minWidth: 200 }}
            helperText="Where the TV stands"
            inputProps={{ "data-testid": "audience-country" }}
          >
            <MenuItem value="">Everywhere</MenuItem>
            {(axes?.countries ?? []).map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label} ({c.premiseCount})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Region"
            value={filters.region ?? ""}
            onChange={(e) => set({ region: e.target.value || undefined })}
            sx={{ minWidth: 180 }}
            inputProps={{ "data-testid": "audience-region" }}
          >
            <MenuItem value="">Everywhere</MenuItem>
            {(axes?.regions ?? []).map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          {/* The axis is `propertyType`. `segment` is a different, care-derived
              premise field and is deliberately not a filter — see the shared
              type for the ruling. */}
          <TextField
            select
            size="small"
            label="Property type"
            value={filters.propertyType ?? ""}
            onChange={(e) =>
              set({
                propertyType:
                  (e.target.value as ContentAnalyticsFilters["propertyType"]) ||
                  undefined,
              })
            }
            sx={{ minWidth: 170 }}
            inputProps={{ "data-testid": "audience-property-type" }}
          >
            <MenuItem value="">All kinds of place</MenuItem>
            {(axes?.propertyTypes ?? []).map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label} ({p.premiseCount})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Channel"
            value={filters.channelAssetId ?? ""}
            onChange={(e) =>
              set({ channelAssetId: e.target.value || undefined })
            }
            sx={{ minWidth: 180 }}
            inputProps={{ "data-testid": "audience-channel" }}
          >
            <MenuItem value="">Every channel</MenuItem>
            {(axes?.channels ?? []).map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>

          <CategoryFilter
            axes={axes}
            value={filters.categoryId}
            onChange={(categoryId) => set({ categoryId })}
          />
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !data && (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {data && (
        <>
          {data.asset.scope.notes.map((note) => (
            <Alert key={note} severity="info">
              {note}
            </Alert>
          ))}

          <Box sx={panel}>
            <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
              <EngagementMetric label="Plays" value={totals.plays} />
              <EngagementMetric
                label="Watch time"
                value={formatDuration(totals.seconds)}
              />
              <EngagementMetric
                label="Completions"
                value={totals.completions}
                hint={
                  totals.plays
                    ? `${formatPercent(totals.completions / totals.plays)} of plays`
                    : undefined
                }
              />
              {/* Never added to plays. An intent is not a viewing. */}
              <EngagementMetric
                label="Impressions"
                value={totals.impressions}
                hint="Play links opened"
              />
              <EngagementMetric
                label="Properties reached"
                value={data.supplier.suppliers[0]?.distinctPremises ?? 0}
                hint="Distinct properties, not rooms"
              />
            </Stack>
            <CoverageStrip coverage={data.asset.coverage} />
          </Box>

          <Box sx={panel}>
            <Tabs
              value={tab}
              onChange={(_, next: TabKey) => setTab(next)}
              sx={{ mb: 2 }}
            >
              <Tab value="assets" label="Titles" />
              <Tab value="packages" label="Packages" />
              <Tab value="channels" label="Channels" />
              <Tab value="reach" label="Reach over time" />
            </Tabs>

            {tab === "assets" && <AssetTable data={data} />}
            {tab === "packages" && <PackageTable data={data} />}
            {tab === "channels" && <ChannelTable data={data} />}
            {tab === "reach" && <Trend data={data} />}
          </Box>

          <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
            Every figure is a sum of per-property, per-day counts reported by
            the televisions themselves. No guest, room, session or device is
            identified at any point, which is also why reach is counted in
            properties rather than rooms. Figures are kept for{" "}
            {Math.round(data.asset.coverage.retentionDays / 365)} years and then
            deleted automatically.
          </Typography>
        </>
      )}
    </Stack>
  );
}

/**
 * The category picker, which may itself be absent.
 *
 * Categories come from the content catalogue, and a catalogue Foyer cannot
 * reach is not "no categories" — an empty dropdown with no explanation is a
 * quiet lie about the platform.
 */
function CategoryFilter({
  axes,
  value,
  onChange,
}: {
  axes: ContentAnalyticsAxes | null;
  value?: string;
  onChange: (value: string | undefined) => void;
}) {
  const categories = axes?.categories;
  if (categories && !Array.isArray(categories)) {
    return (
      <Tooltip title={categories.detail}>
        <Chip
          size="small"
          icon={<InfoOutlinedIcon />}
          label="Category unavailable"
          variant="outlined"
          sx={{ alignSelf: "center" }}
        />
      </Tooltip>
    );
  }
  return (
    <TextField
      select
      size="small"
      label="Category"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || undefined)}
      sx={{ minWidth: 180 }}
      inputProps={{ "data-testid": "audience-category" }}
    >
      <MenuItem value="">Every category</MenuItem>
      {(Array.isArray(categories) ? categories : []).map((c) => (
        <MenuItem key={c.value} value={c.value}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * What the window actually covered.
 *
 * The single most important line on the page: without it a supplier reads a
 * quiet fortnight as a measured zero.
 */
function CoverageStrip({ coverage }: { coverage: ContentPlayCoverage }) {
  const gaps = coverage.days - coverage.daysWithData;
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }} data-testid="audience-coverage">
      <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
        {coverage.requestedFrom} to {coverage.requestedTo} ·{" "}
        {coverage.daysWithData} of {coverage.days} days carry data
        {coverage.observedFrom
          ? ` (first ${coverage.observedFrom}, last ${coverage.observedTo})`
          : ""}
        .
      </Typography>
      {gaps > 0 && (
        <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
          {gaps} day{gaps === 1 ? "" : "s"} reported nothing at all. That is
          silence, not zero viewing — those days are simply not in the figures
          above.
        </Typography>
      )}
      {coverage.truncatedByRetention && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This period reaches further back than we keep data. The missing days
          were deleted on schedule, not left empty.
        </Alert>
      )}
    </Stack>
  );
}

/** An absence, drawn as a reason. Never as a zero and never as an empty box. */
function Absent({ what }: { what: Unavailable }) {
  return (
    <Alert severity="info" icon={<InfoOutlinedIcon />} data-testid="absent">
      {what.detail}
    </Alert>
  );
}

function AssetTable({ data }: { data: ContentAnalyticsOverview }) {
  const rows = data.asset.assets;
  if (!rows.length) {
    return (
      <Typography sx={{ color: tokens.inkMuted }}>
        Nothing of yours played in this period. Titles appear here the first
        time a guest starts one.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small" data-testid="audience-assets">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell align="right">Plays</TableCell>
            <TableCell align="right">Watch time</TableCell>
            <TableCell align="right">Avg per play</TableCell>
            <TableCell align="right">Completed</TableCell>
            <TableCell align="right">Impressions</TableCell>
            <TableCell align="right">Properties</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.kind} ${row.assetId}`} hover>
              <TableCell>
                <Typography variant="body2" noWrap title={row.assetId}>
                  {row.label}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                  {row.kind}
                </Typography>
              </TableCell>
              <TableCell align="right">{row.plays.toLocaleString()}</TableCell>
              <TableCell align="right">
                {formatDuration(row.secondsPlayed)}
              </TableCell>
              <TableCell align="right">
                {formatDuration(row.avgSecondsPerPlay)}
              </TableCell>
              {/* Not clamped to 1: a completion whose start fell before the
                  window opened is real, and a rate above 100% says the window
                  is too narrow for the question rather than that we miscounted. */}
              <TableCell align="right">
                {formatPercent(row.completionRate)}
              </TableCell>
              <TableCell align="right">
                {row.impressions.toLocaleString()}
              </TableCell>
              <TableCell align="right">{row.distinctPremises}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PackageTable({ data }: { data: ContentAnalyticsOverview }) {
  const { packages, unresolved } = data.package;
  return (
    <Stack spacing={2}>
      <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
        A title can be in several packages, so these rows overlap and do not add
        up to your totals.
      </Typography>
      {packages.length === 0 ? (
        <Typography sx={{ color: tokens.inkMuted }}>
          None of your titles played from a package in this period.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="audience-packages">
            <TableHead>
              <TableRow>
                <TableCell>Package</TableCell>
                <TableCell align="right">Your titles</TableCell>
                <TableCell align="right">Plays</TableCell>
                <TableCell align="right">Watch time</TableCell>
                <TableCell align="right">Properties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((row) => (
                <TableRow key={row.packageId} hover>
                  <TableCell>
                    {row.label}
                    {!row.enabled && (
                      <Chip
                        size="small"
                        label="Paused"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{row.assetCount}</TableCell>
                  <TableCell align="right">
                    {row.plays.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(row.secondsPlayed)}
                  </TableCell>
                  <TableCell align="right">{row.distinctPremises}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 🚨 Absent, and labelled absent. These packages are rules over a whole
          catalogue rather than fixed lists, and this report does not enumerate
          them — so their plays are UNMEASURED, which is a different statement
          from zero and is made in those words. */}
      {unresolved.length > 0 && (
        <Alert severity="info" data-testid="audience-packages-unresolved">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {unresolved.length} package
            {unresolved.length === 1 ? " is" : "s are"} not measured here:
          </Typography>
          <Stack spacing={0.5}>
            {unresolved.map((row) => (
              <Typography key={row.packageId} variant="caption">
                <strong>{row.label}</strong> — {row.detail}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

function ChannelTable({ data }: { data: ContentAnalyticsOverview }) {
  const rows = data.channel.channels;
  return (
    <Stack spacing={2}>
      {rows.length === 0 ? (
        <Typography sx={{ color: tokens.inkMuted }}>
          None of your channels were watched in this period.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="audience-channels">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell align="right">Tune-ins</TableCell>
                <TableCell align="right">Watch time</TableCell>
                <TableCell align="right">Avg per tune-in</TableCell>
                <TableCell align="right">Properties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.assetId} hover>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align="right">
                    {row.plays.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(row.secondsPlayed)}
                  </TableCell>
                  <TableCell align="right">
                    {formatDuration(row.avgSecondsPerPlay)}
                  </TableCell>
                  <TableCell align="right">{row.distinctPremises}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Absent what={data.channel.programmes} />
    </Stack>
  );
}

/**
 * The trend, as a bar per day.
 *
 * Deliberately drawn from the SERIES the server returned rather than from a
 * day range filled in on the client: a missing day is missing, and inventing a
 * zero bar for it is precisely the lie the coverage strip exists to prevent.
 */
function Trend({ data }: { data: ContentAnalyticsOverview }) {
  const series = data.asset.series;
  const max = series.reduce((m, d) => Math.max(m, d.plays), 0);
  if (!series.length) {
    return (
      <Typography sx={{ color: tokens.inkMuted }}>
        No day in this period reported any viewing of your material.
      </Typography>
    );
  }
  return (
    <Stack spacing={1} data-testid="audience-trend">
      <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
        Days with no bar reported nothing at all — they are absent from this
        chart rather than drawn as zero.
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ pt: 1 }}>
        {series.map((day) => (
          <Tooltip
            key={day.day}
            title={`${day.day}: ${day.plays} plays, ${formatDuration(
              day.secondsPlayed
            )}`}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 4,
                height: max ? Math.max(4, (day.plays / max) * 120) : 4,
                // Series 1: "what the television counted" - the same
                // ink the CMS uses for attention, so a supplier and an
                // operator reading the same number see the same colour.
                background: tokens.vizAttention,
                borderRadius: 0.5,
                opacity: 0.8,
              }}
            />
          </Tooltip>
        ))}
      </Stack>
    </Stack>
  );
}
